import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import React, { useState } from 'react';
import spaceImage from '../images/space.webp';
import { useRecaptcha } from '../hooks/useRecaptcha';
import { authApi } from '../services/api';
import { validateUsername } from '../utils/validation';
import HostedInvoiceDialog from './HostedInvoiceDialog';
import PaymentProcessingOverlay from './PaymentProcessingOverlay';
import SubscriptionCalloutBanner from './SubscriptionCalloutBanner';
import TermsAndPrivacy from './TermsAndPrivacy';

interface SignupFormData {
    email: string;
    userName: string;
    password: string;
    confirmPassword: string;
}

const CheckoutForm: React.FC<{
    formData: SignupFormData;
    onSuccess: (result: any) => void;
    onError: (error: string) => void;
    isProcessing: boolean;
    setIsProcessing: (processing: boolean) => void;
    validateForm: () => string | null;
    onSubscriptionCreation: (paymentMethodId: string) => void;
}> = ({
    formData,
    onSuccess,
    onError,
    isProcessing,
    setIsProcessing,
    validateForm,
    onSubscriptionCreation,
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isPaymentInfoComplete, setIsPaymentInfoComplete] = useState(false);

    // Check if all form fields are filled and payment info is complete
    const isFormComplete = () => {
        return (
            formData.email.trim() !== '' &&
            formData.userName.trim() !== '' &&
            formData.password.trim() !== '' &&
            formData.confirmPassword.trim() !== '' &&
            isPaymentInfoComplete
        );
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        // Validate form data before proceeding with payment
        const validationError = validateForm();
        if (validationError) {
            onError(validationError);
            return;
        }

        if (!stripe || !elements) {
            return;
        }

        try {
            // Set processing state to show spinner and disable button
            setIsProcessing(true);

            await elements.submit();

            // Create payment method
            const { error, paymentMethod } = await stripe.createPaymentMethod({
                elements,
            });

            if (error) {
                onError(error.message || 'Payment method creation failed');
                setIsProcessing(false);
                return;
            }

            if (paymentMethod) {
                // Call the subscription creation function with the payment method ID
                onSubscriptionCreation(paymentMethod.id);
            }
        } catch (err: any) {
            onError(err.message || 'An unexpected error occurred');
        }
    };

    return (
        <>
            <div className='space-y-6'>
                <div className='mb-6'>
                    <h3 className='text-xl font-semibold text-gray-900 mb-2'>
                        Payment Information
                    </h3>
                    <p className='text-sm text-gray-600'>
                        Enter your payment details to complete your account creation
                    </p>
                </div>

                <PaymentElement
                    className='mb-6'
                    onChange={event => {
                        setIsPaymentInfoComplete(event.complete);
                    }}
                />

                <SubscriptionCalloutBanner />

                <button
                    type='submit'
                    disabled={!stripe || !elements || isProcessing || !isFormComplete()}
                    className='w-full bg-gradient-to-r from-yellow-400 to-orange-500 !text-black py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium disabled:transform-none'
                    onClick={handleSubmit}
                >
                    Start Subscription ($3/month)
                </button>
                <TermsAndPrivacy />

                {/* Show what fields are still needed */}
                {!isFormComplete() && (
                    <div className='mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg'>
                        <div className='flex items-start'>
                            <svg
                                className='w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                            >
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth='2'
                                    d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                                ></path>
                            </svg>
                            <div className='text-sm text-amber-800'>
                                <p className='font-medium mb-1'>Complete all fields to proceed:</p>
                                <ul className='space-y-1 text-xs'>
                                    {!formData.email.trim() && <li>• Email address</li>}
                                    {!formData.userName.trim() && <li>• Username</li>}
                                    {!formData.password.trim() && <li>• Password</li>}
                                    {!formData.confirmPassword.trim() && (
                                        <li>• Confirm password</li>
                                    )}
                                    {!isPaymentInfoComplete && <li>• Payment information</li>}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <PaymentProcessingOverlay isVisible={isProcessing} />
        </>
    );
};

const SignupWithStripe: React.FC = () => {
    const [formData, setFormData] = useState<SignupFormData>({
        email: '',
        userName: '',
        password: '',
        confirmPassword: '',
    });
    const [error, setError] = useState<string>('');
    const [usernameError, setUsernameError] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [usernameValidation, setUsernameValidation] = useState<{
        isValid: boolean;
        error?: string;
    }>({ isValid: true });
    const [successData, setSuccessData] = useState<any>(null);
    const [hostedInvoiceData, setHostedInvoiceData] = useState<{
        url: string;
    } | null>(null);
    // Use the reCAPTCHA hook
    const { getToken } = useRecaptcha();

    // Handle subscription creation with payment method
    const handleSubscriptionCreation = async (paymentMethodId: string) => {
        // Validate form data before proceeding
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        try {
            setIsProcessing(true);
            setError('');
            setUsernameError('');

            // Get reCAPTCHA token using the hook
            const token = await getToken('subscription_creation');

            // Call attemptSubscriptionCreation API
            const response = await authApi.attemptSubscriptionCreation({
                email: formData.email,
                userName: formData.userName,
                password: formData.password,
                confirmPassword: formData.confirmPassword,
                paymentMethodId: paymentMethodId,
                recaptchaToken: token,
            });

            if (response.hostedInvoiceUrl) {
                console.log('Hosted invoice URL:', response.hostedInvoiceUrl);
                // Show dialog instead of redirecting
                setHostedInvoiceData({
                    url: response.hostedInvoiceUrl,
                });
                return;
            }

            if (response.status === 'success') {
                setSuccessData({
                    user: {
                        email: formData.email,
                        userName: formData.userName,
                    },
                    playerId: response.playerId,
                    subscriptionId: response.subscriptionId,
                });
            } else {
                // Handle specific error codes
                if (response.message?.includes('already exists')) {
                    setError('You already have an account. Please log in first.');
                } else if (response.message?.includes('username')) {
                    setUsernameError(
                        `Username "${formData.userName}" is already used, please select another name`
                    );
                } else if (response.message?.includes('disabled')) {
                    setUsernameError('Signups currently disabled');
                } else {
                    setError(response.message || 'Failed to create subscription');
                }
            }
        } catch (err: any) {
            console.error('Subscription creation failed:', err);
            setError('Subscription creation failed. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleInputChange =
        (field: keyof SignupFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;

            setFormData(prev => ({
                ...prev,
                [field]: value,
            }));

            // Clear username error when username is changed
            if (field === 'userName') {
                setUsernameError('');
                setError('');

                // Real-time validation for username using common validation function
                if (value.trim()) {
                    const validation = validateUsername(value);
                    setUsernameValidation(validation);
                } else {
                    setUsernameValidation({ isValid: true });
                }
            }
        };

    const validateForm = () => {
        if (
            !formData.email ||
            !formData.userName ||
            !formData.password ||
            !formData.confirmPassword
        ) {
            return 'All fields are required';
        }

        // Validate username using common validation function
        const usernameValidation = validateUsername(formData.userName);
        if (!usernameValidation.isValid) {
            return usernameValidation.error!;
        }

        if (formData.password !== formData.confirmPassword) {
            return 'Passwords do not match';
        }

        if (formData.password.length < 8) {
            return 'Password must be at least 8 characters long';
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            return 'Please enter a valid email address';
        }

        return null;
    };

    // Show hosted invoice dialog
    if (hostedInvoiceData) {
        return <HostedInvoiceDialog hostedInvoiceData={hostedInvoiceData} />;
    }

    if (successData) {
        return (
            <div
                className='min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8'
                style={{
                    backgroundImage: `url(${spaceImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className='max-w-md w-full mx-auto p-8 bg-white rounded-2xl shadow-xl border border-gray-100 text-center'>
                    <div className='mb-6'>
                        <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                            <svg
                                className='w-8 h-8 text-green-600'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                            >
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth='2'
                                    d='M5 13l4 4L19 7'
                                ></path>
                            </svg>
                        </div>
                    </div>
                    <div className='text-sm text-gray-500 mb-6 p-4 bg-gray-50 rounded-lg'>
                        <>
                            <p>Email: {successData?.user?.email}</p>
                            <p>Username: {successData?.user?.userName}</p>
                        </>
                    </div>
                    <button
                        onClick={() => (window.location.href = '/login')}
                        className='w-full bg-gradient-to-r from-yellow-400 to-orange-500 !text-black py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200'
                    >
                        Continue to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            className='min-h-screen py-8'
            style={{
                backgroundImage: `url(${spaceImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                backgroundRepeat: 'no-repeat',
            }}
        >
            <div className='max-w-6xl mx-auto px-4'>
                <div className='text-center mb-8'>
                    <h1 className='text-4xl font-bold text-orange-500 mb-2'>Create Your Account</h1>
                    <p className='text-lg text-white'>Join PuzzlePlanet today</p>
                </div>

                <div className='grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto'>
                    {/* Signup Form */}
                    <div className='bg-white rounded-2xl shadow-xl border border-gray-100 p-8'>
                        <h2 className='text-2xl font-semibold text-gray-900 mb-6'>
                            Account Information
                        </h2>

                        <div className='space-y-6'>
                            <div>
                                <label
                                    htmlFor='email'
                                    className='block text-sm font-medium text-gray-700 mb-2'
                                >
                                    Email Address
                                </label>
                                <input
                                    id='email'
                                    type='email'
                                    value={formData.email}
                                    autoComplete='off'
                                    onChange={handleInputChange('email')}
                                    className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200'
                                    required
                                    placeholder='Enter your email address'
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor='userName'
                                    className='block text-sm font-medium text-gray-700 mb-2'
                                >
                                    Username
                                </label>
                                <input
                                    id='userName'
                                    type='text'
                                    value={formData.userName}
                                    autoComplete='off'
                                    onChange={handleInputChange('userName')}
                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-offset-0 transition-colors duration-200 ${
                                        formData.userName.trim() && !usernameValidation.isValid
                                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                            : formData.userName.trim() && usernameValidation.isValid
                                            ? 'border-green-300 focus:ring-green-500 focus:border-green-500'
                                            : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                    }`}
                                    required
                                    placeholder='Choose a username (3-20 chars)'
                                />
                                <p className='mt-2 text-sm text-gray-500'>
                                    Allowed characters: letters, numbers, underscores (_), and
                                    hyphens (-)
                                </p>
                                {/* Username validation message */}
                                {formData.userName.trim() && !usernameValidation.isValid && (
                                    <p className='mt-1 text-sm text-red-600'>
                                        {usernameValidation.error}
                                    </p>
                                )}
                                {(usernameError || error) && (
                                    <div className='mt-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm'>
                                        {usernameError && (
                                            <div className='mb-2'>{usernameError}</div>
                                        )}
                                        {error && (
                                            <div className='flex flex-col space-y-2'>
                                                <span>{error}</span>
                                                {error ===
                                                    'You already have an account. Please log in first.' && (
                                                    <a
                                                        href='/login'
                                                        className='text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200 underline'
                                                    >
                                                        Go to Login Page
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label
                                    htmlFor='password'
                                    className='block text-sm font-medium text-gray-700 mb-2'
                                >
                                    Password
                                </label>
                                <input
                                    id='password'
                                    type='password'
                                    autoComplete='new-password'
                                    value={formData.password}
                                    onChange={handleInputChange('password')}
                                    className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                                    required
                                    placeholder='Enter a password (min 8 characters)'
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor='confirmPassword'
                                    className='block text-sm font-medium text-gray-700 mb-2'
                                >
                                    Confirm Password
                                </label>
                                <input
                                    id='confirmPassword'
                                    type='password'
                                    autoComplete='new-password'
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange('confirmPassword')}
                                    className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                                    required
                                    placeholder='Confirm your password'
                                />
                            </div>
                        </div>

                        <div className='mt-8 text-center'>
                            <p className='text-sm text-gray-600'>
                                Already have an account?{' '}
                                <a
                                    href='/login'
                                    className='text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200'
                                >
                                    Sign in
                                </a>
                            </p>
                        </div>
                    </div>

                    {/* Payment Form */}
                    <div className='bg-white rounded-2xl shadow-xl border border-gray-100 p-8'>
                        <h2 className='text-2xl font-semibold text-gray-900 mb-6'>
                            Payment Information
                        </h2>

                        <div className='space-y-6'>
                            <div className='p-6 bg-gray-50 rounded-xl border border-gray-200'>
                                <div className='flex items-center justify-between mb-3'>
                                    <span className='font-semibold text-gray-900'>
                                        Puzzle Planet Subscription
                                    </span>
                                    <span className='text-lg font-bold text-blue-600'>
                                        $3.00 per month
                                    </span>
                                </div>
                                <p className='text-sm text-gray-600'>
                                    Monthly subscription for your PuzzlePlanet account
                                </p>
                            </div>

                            <div className='p-6 bg-blue-50 rounded-xl border border-blue-200'>
                                <div className='flex items-center mb-3'>
                                    <svg
                                        className='w-5 h-5 text-blue-600 mr-2'
                                        fill='none'
                                        stroke='currentColor'
                                        viewBox='0 0 24 24'
                                    >
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth='2'
                                            d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                                        ></path>
                                    </svg>
                                    <span className='text-sm font-medium text-blue-900'>
                                        Secure Payment
                                    </span>
                                </div>
                                <p className='text-sm text-blue-700'>
                                    Your payment information is securely processed by Stripe. We
                                    never store your payment details.
                                </p>
                            </div>

                            <CheckoutForm
                                formData={formData}
                                onSuccess={result => setSuccessData(result)}
                                onError={error => setError(error)}
                                isProcessing={isProcessing}
                                setIsProcessing={setIsProcessing}
                                validateForm={validateForm}
                                onSubscriptionCreation={handleSubscriptionCreation}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignupWithStripe;
